import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, Zap, ShieldCheck, Users } from 'lucide-react';

const ASISANT_LOGO = 'https://customer-assets.emergentagent.com/job_asisant-portal/artifacts/sagigh38_logo_w.png';

// Scene geometry
const W = 760;
const H = 560;
const CX = W / 2;
const CY = H / 2 - 20;

const TAU = Math.PI * 2;

const OUTER = [
  { name: 'Microsoft', mono: 'MS', color: '#00A4EF' },
  { name: 'AWS', mono: 'AWS', color: '#FF9900' },
  { name: 'Google', mono: 'G', color: '#4285F4' },
  { name: 'Adobe', mono: 'Ad', color: '#FF4444' },
  { name: 'Oracle', mono: 'O', color: '#C74634' },
  { name: 'Cisco', mono: 'C', color: '#1BA0D7' },
  { name: 'IBM', mono: 'IBM', color: '#0F62FE' },
  { name: 'Zoom', mono: 'Z', color: '#4087FC' },
];

const INNER = [
  { name: 'Acronis', mono: 'A', color: '#00B2E3' },
  { name: 'Dropbox', mono: 'D', color: '#0061FF' },
  { name: 'ESET', mono: 'E', color: '#13A5DC' },
  { name: 'Sophos', mono: 'S', color: '#00A0DF' },
  { name: 'Kaspersky', mono: 'K', color: '#00A88E' },
  { name: 'Bitdefender', mono: 'B', color: '#ED1C24' },
  { name: 'GitHub', mono: 'GH', color: '#A5B4FC' },
];

const VENDORS = [
  ...OUTER.map((v, i) => ({ ...v, rx: 318, ry: 126, speed: 0.11, base: (i / OUTER.length) * TAU })),
  ...INNER.map((v, i) => ({ ...v, rx: 192, ry: 76, speed: -0.18, base: (i / INNER.length) * TAU + 0.4 })),
];

const PACKET_COUNT = 6;

const ACTIVITY = [
  { icon: CheckCircle2, color: 'text-green-400', text: 'Microsoft 365 Business — Licencia aprovisionada' },
  { icon: RefreshCw, color: 'text-[#8bcaf9]', text: 'Adobe VIP — Precios sincronizados vía API' },
  { icon: Zap, color: 'text-yellow-300', text: 'AWS — Suscripción activada en 12s' },
  { icon: ShieldCheck, color: 'text-[#40aaf8]', text: 'Acronis Cyber Protect — Renovación automática' },
  { icon: Users, color: 'text-green-400', text: 'Google Workspace — 25 puestos añadidos' },
];

const ActivityFeed = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setIdx((p) => (p + 1) % ACTIVITY.length), 3000);
    return () => clearInterval(interval);
  }, []);

  const item = ACTIVITY[idx];
  const Icon = item.icon;

  return (
    <div
      key={idx}
      data-testid="hero-activity-toast"
      className="activity-enter flex items-center gap-2.5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl px-4 py-3 shadow-2xl"
    >
      <Icon className={`w-4 h-4 shrink-0 ${item.color}`} />
      <span className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">{item.text}</span>
    </div>
  );
};

const VendorConnectivityScene = () => {
  const tiltRef = useRef(null);
  const nodeRefs = useRef([]);
  const lineRefs = useRef([]);
  const packetRefs = useRef([]);

  useEffect(() => {
    const positions = VENDORS.map(() => [CX, CY]);
    const packets = Array.from({ length: PACKET_COUNT }, (_, i) => ({
      node: Math.floor(Math.random() * VENDORS.length),
      prog: Math.random(),
      speed: 0.3 + Math.random() * 0.35,
      dir: i % 3 === 0 ? -1 : 1, // some packets travel hub -> vendor
    }));

    let raf;
    let last = performance.now();
    const start = last;

    const tick = (now) => {
      const t = (now - start) / 1000;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      // Orbiting vendor nodes + connection beams
      VENDORS.forEach((n, i) => {
        const ang = n.base + t * n.speed;
        const x = CX + n.rx * Math.cos(ang);
        const y = CY + n.ry * Math.sin(ang);
        const depth = (Math.sin(ang) + 1) / 2; // 0 = behind hub, 1 = in front
        positions[i] = [x, y];

        const el = nodeRefs.current[i];
        if (el) {
          el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${0.6 + 0.45 * depth})`;
          el.style.opacity = 0.4 + 0.6 * depth;
          el.style.zIndex = String(Math.round(10 + depth * 20));
        }
        const line = lineRefs.current[i];
        if (line) {
          line.setAttribute('x1', x.toFixed(1));
          line.setAttribute('y1', y.toFixed(1));
          line.style.opacity = (0.07 + 0.23 * depth).toFixed(2);
        }
      });

      // Data packets traveling along the beams
      packets.forEach((p, pi) => {
        p.prog += p.speed * dt;
        if (p.prog >= 1) {
          p.prog = 0;
          p.node = Math.floor(Math.random() * VENDORS.length);
          p.speed = 0.3 + Math.random() * 0.35;
        }
        const [vx, vy] = positions[p.node];
        const tt = p.dir === 1 ? p.prog : 1 - p.prog; // vendor->hub or hub->vendor
        const ease = tt * tt * (3 - 2 * tt); // smoothstep
        const px = vx + (CX - vx) * ease;
        const py = vy + (CY - vy) * ease;
        const dot = packetRefs.current[pi];
        if (dot) {
          dot.setAttribute('cx', px.toFixed(1));
          dot.setAttribute('cy', py.toFixed(1));
          dot.setAttribute('r', (4.5 - 2 * ease).toFixed(1));
        }
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Mouse parallax tilt (subtle 3D)
  const handleMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = (e.clientX - rect.left) / rect.width - 0.5;
    const dy = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(1100px) rotateY(${dx * 9}deg) rotateX(${-dy * 9}deg)`;
  };

  const handleLeave = () => {
    const el = tiltRef.current;
    if (el) el.style.transform = 'perspective(1100px) rotateY(0deg) rotateX(0deg)';
  };

  return (
    <div
      data-testid="hero-connectivity-scene"
      className="relative"
      style={{ width: W, height: H }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <div
        ref={tiltRef}
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Connection beams + packets (SVG layer) */}
        <svg className="absolute inset-0 overflow-visible" width={W} height={H} aria-hidden="true">
          <defs>
            <radialGradient id="packetGlow">
              <stop offset="0%" stopColor="#bfe2ff" />
              <stop offset="100%" stopColor="#4e95d9" />
            </radialGradient>
          </defs>
          {VENDORS.map((v, i) => (
            <line
              key={`beam-${v.name}`}
              ref={(el) => (lineRefs.current[i] = el)}
              x1={CX}
              y1={CY}
              x2={CX}
              y2={CY}
              stroke="#8bcaf9"
              strokeWidth="1"
              strokeDasharray="3 5"
            />
          ))}
          {Array.from({ length: PACKET_COUNT }).map((_, i) => (
            <circle
              key={`packet-${i}`}
              ref={(el) => (packetRefs.current[i] = el)}
              cx={CX}
              cy={CY}
              r="3.5"
              fill="url(#packetGlow)"
              style={{ filter: 'drop-shadow(0 0 6px rgba(139,202,249,0.9))' }}
            />
          ))}
        </svg>

        {/* Elliptical orbit guides */}
        <svg className="absolute inset-0 overflow-visible" width={W} height={H} aria-hidden="true">
          <ellipse cx={CX} cy={CY} rx="318" ry="126" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <ellipse cx={CX} cy={CY} rx="192" ry="76" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
        </svg>

        {/* Central marketplace hub */}
        <div
          data-testid="hero-marketplace-hub"
          className="absolute"
          style={{ left: CX, top: CY, transform: 'translate(-50%, -50%)', zIndex: 25 }}
        >
          {/* Pulse rings */}
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="hub-pulse-ring absolute left-1/2 top-1/2 w-32 h-32 rounded-full border border-[#8bcaf9]/50"
              style={{ animationDelay: `${i * 1.1}s` }}
            />
          ))}
          {/* Rotating dashed ring */}
          <span className="spin-slow absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 rounded-full border-2 border-dashed border-white/20" />
          <span className="spin-slow-reverse absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full border border-[#4e95d9]/40" />
          {/* Core */}
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#103a5e] to-[#06121f] border border-[#4e95d9]/60 shadow-[0_0_60px_rgba(78,149,217,0.55)] flex flex-col items-center justify-center gap-1.5">
            <img src={ASISANT_LOGO} alt="asisant" className="h-7 w-auto object-contain" />
            <span className="text-[#8bcaf9] text-[10px] font-bold uppercase tracking-[0.18em]">Marketplace</span>
          </div>
        </div>

        {/* Orbiting vendor nodes */}
        {VENDORS.map((v, i) => (
          <div
            key={v.name}
            ref={(el) => (nodeRefs.current[i] = el)}
            data-testid={`vendor-node-${v.name.toLowerCase()}`}
            className="absolute left-0 top-0 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full pl-1.5 pr-3.5 py-1.5 shadow-lg will-change-transform"
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
              style={{ background: `linear-gradient(135deg, ${v.color}, ${v.color}99)` }}
            >
              {v.mono}
            </span>
            <span className="text-white text-xs font-semibold whitespace-nowrap">{v.name}</span>
          </div>
        ))}
      </div>

      {/* Live activity feed */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2" style={{ zIndex: 40 }}>
        <ActivityFeed />
      </div>
    </div>
  );
};

export default VendorConnectivityScene;
